import React, { useState, useEffect } from 'react';
import { useFilters } from '../context/FilterContext';
import { API_BASE } from '../services/apiService';
import { Filter, RotateCcw, X, ChevronRight, ChevronLeft } from 'lucide-react';
import { formatFullIndianCurrency } from '../utils/formatters';

const FilterBar = () => {
  const { filters, updateFilter, resetFilters, drillFilters, clearDrillFilter, clearAllDrillFilters } = useFilters();
  const [employeeIds, setEmployeeIds] = useState([]);
  const [sumInsureds, setSumInsureds] = useState([]);
  const [open, setOpen] = useState(true);
  const [manualToggle, setManualToggle] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/filter-options`).then(r => r.json()).then(d => {
      setEmployeeIds(d.employeeIds || []);
      setSumInsureds(d.sumInsureds || []);
    }).catch(() => {});
  }, []);

  // Auto-collapse on scroll with hysteresis to prevent flicker
  // Collapse at 300px, but only re-open at 100px (200px dead zone prevents toggling)
  const scrollState = React.useRef({ isOpen: true, timeout: null });
  useEffect(() => {
    const onScroll = () => {
      if (manualToggle) return;
      const y = window.scrollY;
      const { isOpen } = scrollState.current;
      if (isOpen && y > 300) {
        // Collapse — delay slightly to avoid flicker
        if (!scrollState.current.timeout) {
          scrollState.current.timeout = setTimeout(() => {
            scrollState.current.isOpen = false;
            scrollState.current.timeout = null;
            setOpen(false);
          }, 100);
        }
      } else if (!isOpen && y < 100) {
        // Re-open only when scrolled back near top
        scrollState.current.isOpen = true;
        if (scrollState.current.timeout) {
          clearTimeout(scrollState.current.timeout);
          scrollState.current.timeout = null;
        }
        setOpen(true);
      } else if (isOpen && y <= 300 && scrollState.current.timeout) {
        // Cancel pending collapse if user scrolled back up
        clearTimeout(scrollState.current.timeout);
        scrollState.current.timeout = null;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (scrollState.current.timeout) clearTimeout(scrollState.current.timeout);
    };
  }, [manualToggle]);

  // Count active filters
  const activeCount = [
    filters.dateRange?.start,
    filters.dateRange?.end,
    filters.claimType !== 'All' ? filters.claimType : null,
    filters.claimStatus !== 'All' ? filters.claimStatus : null,
    filters.relation !== 'All' ? filters.relation : null,
    filters.cityType !== 'All' ? filters.cityType : null,
    filters.employeeId,
    filters.sumInsured && filters.sumInsured !== 'All' ? filters.sumInsured : null,
  ].filter(Boolean).length + Object.keys(drillFilters).length;

  const selectClass = "text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 bg-white text-gray-700 min-w-[120px]";
  const inputClass = "text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 bg-white text-gray-700";

  const drillEntries = Object.entries(drillFilters);

  return (
    <div className={`${open ? 'sticky top-[88px]' : ''} z-30 print:hidden`}>
      {/* Collapsed: fixed floating button */}
      {!open && (
        <button
          onClick={() => { setOpen(true); setManualToggle(true); }}
          data-tour="filter-btn"
          className="fixed left-4 top-[94px] flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-purple-700 bg-white border border-purple-200 rounded-lg hover:bg-purple-50 shadow-md z-40"
          style={{animation: 'fadeIn 0.15s ease-out'}}
        >
          <Filter size={13} />
          <span>Filters</span>
          {activeCount > 0 && (
            <span className="flex items-center justify-center bg-purple-600 text-white text-[9px] font-bold rounded-full px-1.5 min-w-[16px] h-4">{activeCount}</span>
          )}
          <ChevronRight size={12} />
        </button>
      )}

      {/* Expanded: full filter strip */}
      {open && (
        <div className="bg-white border-b border-gray-200 px-6 py-3 shadow-sm" data-tour="filter-bar">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => { setOpen(false); setManualToggle(false); }}
              data-tour="filter-btn"
              className="flex items-center gap-1 text-purple-700 mr-1 hover:bg-purple-50 rounded-lg px-2 py-1.5 transition-colors"
              title="Collapse filters"
            >
              <ChevronLeft size={14} />
              <Filter size={14} />
              <span className="text-xs font-semibold uppercase tracking-wider">Filters</span>
            </button>

            <div className="flex items-center gap-1.5">
              <label className="text-xs text-gray-500">From</label>
              <input
                type="date"
                value={filters.dateRange.start || ''}
                onChange={e => updateFilter('dateRange', { ...filters.dateRange, start: e.target.value || null })}
                className={inputClass}
              />
            </div>
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-gray-500">To</label>
              <input
                type="date"
                value={filters.dateRange.end || ''}
                onChange={e => updateFilter('dateRange', { ...filters.dateRange, end: e.target.value || null })}
                className={inputClass}
              />
            </div>

            <select
              value={filters.claimType}
              onChange={e => updateFilter('claimType', e.target.value)}
              className={selectClass}
            >
              <option value="All">All Claim Types</option>
              <option value="Cashless">Cashless</option>
              <option value="Reimbursement">Reimbursement</option>
            </select>

            <select
              value={filters.claimStatus}
              onChange={e => updateFilter('claimStatus', e.target.value)}
              className={selectClass}
            >
              <option value="All">All Statuses</option>
              <option value="Paid">Paid</option>
              <option value="Outstanding">Outstanding</option>
              <option value="Rejected">Rejected</option>
              <option value="Settled">Settled</option>
            </select>

            <select
              value={filters.relation}
              onChange={e => updateFilter('relation', e.target.value)}
              className={selectClass}
            >
              <option value="All">All Relations</option>
              <option value="Self">Self</option>
              <option value="Spouse">Spouse</option>
              <option value="Parents">Parents</option>
              <option value="Children">Children</option>
            </select>

            <select
              value={filters.cityType}
              onChange={e => updateFilter('cityType', e.target.value)}
              className={selectClass}
            >
              <option value="All">All City Types</option>
              <option value="Metro">Metro</option>
              <option value="Non-Metro">Non-Metro</option>
            </select>

            <select
              value={filters.employeeId || ''}
              onChange={e => updateFilter('employeeId', e.target.value)}
              className={selectClass}
            >
              <option value="">All Employees</option>
              {employeeIds.map(id => <option key={id} value={id}>{id}</option>)}
            </select>

            <select
              value={filters.sumInsured || 'All'}
              onChange={e => updateFilter('sumInsured', e.target.value)}
              className={selectClass}
            >
              <option value="All">All Sum Insured</option>
              {sumInsureds.map(si => <option key={si} value={si}>{formatFullIndianCurrency(si)}</option>)}
            </select>

            <button
              onClick={resetFilters}
              className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              title="Reset all filters"
            >
              <RotateCcw size={12} /> Reset
            </button>
          </div>

          {/* Active drill-down filters */}
          {drillEntries.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-gray-100">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Drill-down:</span>
              {drillEntries.map(([key, value]) => (
                <span key={key} className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium bg-purple-100 text-purple-800 rounded-full">
                  <span className="text-purple-500">{key}:</span> {value}
                  <button onClick={() => clearDrillFilter(key)} className="ml-0.5 hover:text-red-600 transition-colors">
                    <X size={12} />
                  </button>
                </span>
              ))}
              <button
                onClick={clearAllDrillFilters}
                className="text-[10px] font-medium text-red-500 hover:text-red-700 ml-1 transition-colors"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterBar;
